import type { VendorCode } from 'utils/vendorDisplayName'

import type {
  FilterableRecord,
  FilterEngineContext,
  MissionOption,
  RobotOption,
  TasksNormalizedFilterState,
} from './filterableRecord'
import { FACILITY_NONE_ID } from './filterableRecord'

function hourOf(d: Date): number {
  return d.getHours() + d.getMinutes() / 60 + d.getSeconds() / 3600
}

function matchesMissionKey(r: FilterableRecord, missionKey: string): boolean {
  if (!missionKey) return true
  if (missionKey.startsWith('map:')) {
    return r.mapId === missionKey.slice(4)
  }
  const mid = Number(missionKey)
  if (!Number.isFinite(mid)) return false
  return r.missionId === mid || r.scenarioId === mid || r.missionsInfoIds.includes(mid)
}

export type PartialMatchOpts = {
  ignoreFacility?: boolean
  ignoreVendor?: boolean
  ignoreRobot?: boolean
  ignoreMission?: boolean
  ignoreStartAfter?: boolean
}

/**
 * Filter dimensions with optional "ignore" flags (for cascading dropdown reachability).
 */
export function partialMatches(
  r: FilterableRecord,
  f: TasksNormalizedFilterState,
  opts: PartialMatchOpts,
  _ctx: FilterEngineContext
): boolean {
  if (!opts.ignoreVendor && f.vendor && r.vendor !== f.vendor) return false

  if (!opts.ignoreRobot && f.robotSn && r.robotSn !== f.robotSn) return false

  if (!opts.ignoreStartAfter && f.startAfterHour >= 0 && hourOf(r.startAt) < f.startAfterHour) return false

  if (!opts.ignoreFacility && f.facilityId) {
    if (f.facilityId === FACILITY_NONE_ID) {
      if (r.facilityIds.length > 0) return false
    } else {
      const fid = Number(f.facilityId)
      if (!Number.isFinite(fid) || !r.facilityIds.includes(fid)) return false
    }
  }

  if (!opts.ignoreMission && f.missionKey && !matchesMissionKey(r, f.missionKey)) return false

  return true
}

/**
 * Full row visibility (all active filters AND).
 */
export function matchesFilter(
  r: FilterableRecord,
  f: TasksNormalizedFilterState,
  ctx: FilterEngineContext
): boolean {
  return partialMatches(r, f, {}, ctx)
}

export type SchedulerFilterOptions = {
  vendors: VendorCode[]
  facilityOptions: Array<{ id: string; name: string }>
  robots: RobotOption[]
  missions: MissionOption[]
}

/**
 * Options that still yield at least one row given other selected filters (cascading UI).
 */
export function computeSchedulerFilterOptions(
  records: FilterableRecord[],
  f: TasksNormalizedFilterState,
  ctx: FilterEngineContext
): SchedulerFilterOptions {
  const reachableVendors = new Set<VendorCode>()
  const reachableFacilityKeys = new Set<string>()
  const reachableRobotSns = new Set<string>()
  const missionOptionByKey = new Map<string, MissionOption>()

  for (const r of records) {
    if (partialMatches(r, f, { ignoreFacility: true }, ctx)) {
      if (r.facilityIds.length) {
        for (const fid of r.facilityIds) reachableFacilityKeys.add(String(fid))
      } else if (ctx.facilityMappingReady) {
        reachableFacilityKeys.add(FACILITY_NONE_ID)
      }
    }

    if (partialMatches(r, f, { ignoreVendor: true }, ctx)) {
      reachableVendors.add(r.vendor)
    }

    if (partialMatches(r, f, { ignoreRobot: true }, ctx)) {
      reachableRobotSns.add(r.robotSn)
    }

    if (partialMatches(r, f, { ignoreMission: true }, ctx)) {
      if (r.mapId) {
        const k = `map:${r.mapId}`
        if (!missionOptionByKey.has(k)) {
          missionOptionByKey.set(k, { key: k, label: String(r.mapId), kind: 'map' })
        }
      }
      if (r.scenarioId != null) {
        const k = String(r.scenarioId)
        if (!missionOptionByKey.has(k)) {
          missionOptionByKey.set(k, { key: k, label: `Scenario ${r.scenarioId}`, kind: 'scenario' })
        }
      }
      if (r.missionId != null) {
        const k = String(r.missionId)
        if (!missionOptionByKey.has(k)) {
          missionOptionByKey.set(k, { key: k, label: `Mission ${r.missionId}`, kind: 'mission' })
        }
      }
      for (const mi of r.missionsInfo ?? []) {
        const k = String(mi.id)
        const name = String(mi.name ?? '').trim()
        if (!missionOptionByKey.has(k)) {
          missionOptionByKey.set(k, { key: k, label: name || `Mission ${mi.id}`, kind: 'mission' })
        }
      }
    }
  }

  const facilityOptions = ctx.facilities
    .filter(fac => reachableFacilityKeys.has(String(fac.id)))
    .map(fac => ({ id: String(fac.id), name: fac.name || `Facility ${fac.id}` }))
    .sort((a, b) => a.name.localeCompare(b.name))

  if (reachableFacilityKeys.has(FACILITY_NONE_ID)) {
    facilityOptions.unshift({ id: FACILITY_NONE_ID, name: 'No facility' })
  }

  const robots: RobotOption[] = Array.from(reachableRobotSns)
    .map(sn => {
      const ro = ctx.robotsBySn.get(sn)
      return ro ?? { id: 0, name: sn, sn }
    })
    .sort((a, b) => a.name.localeCompare(b.name))

  const missions: MissionOption[] = Array.from(missionOptionByKey.values()).sort((a, b) => {
    const order = { scenario: 0, mission: 1, map: 2 }
    const byKind = order[a.kind] - order[b.kind]
    if (byKind !== 0) return byKind

    const aNum = Number(a.key)
    const bNum = Number(b.key)
    const aIsNum = Number.isFinite(aNum)
    const bIsNum = Number.isFinite(bNum)
    if (aIsNum && bIsNum) return aNum - bNum
    return a.label.localeCompare(b.label)
  })

  return {
    vendors: Array.from(reachableVendors).sort(),
    facilityOptions,
    robots,
    missions,
  }
}
